import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { getLogoUrl } from '../config';
import { toast } from '../components/Toast';
import DjamannLoader from '../components/DjamannLoader';
import { ClipboardList, User, MessageCircle, RefreshCw, DollarSign, Save, ScrollText, Frown, Check } from 'lucide-react';

const AdminServiceRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [request, setRequest] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Formulaire comptabilité
  const [comptaForm, setComptaForm] = useState({
    montant_estime: '',
    montant_final: '',
    montant_paye: '',
    reference_paiement: '',
    mode_paiement: ''
  });
  
  const messagesEndRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [requestRes, statusesRes, messagesRes] = await Promise.all([
        api.get(`/service-requests/${id}`),
        api.get('/service-statuses'),
        api.get(`/service-requests/${id}/messages`)
      ]);
      
      const requestData = requestRes.data;
      setRequest(requestData);
      setStatuses(Array.isArray(statusesRes.data) ? statusesRes.data : []);
      setMessages(Array.isArray(messagesRes.data) ? messagesRes.data : []);
      
      // Pré-remplir le formulaire de comptabilité
      setComptaForm({
        montant_estime: requestData.montant_estime || '',
        montant_final: requestData.montant_final || '',
        montant_paye: requestData.montant_paye || '',
        reference_paiement: requestData.reference_paiement || '',
        mode_paiement: requestData.mode_paiement || ''
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate, loadData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStatusChange = async (newStatusId) => {
    try {
      setUpdating(true);
      await api.put(`/service-requests/${id}/statut`, { statut_id: newStatusId });
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du changement de statut');
    } finally {
      setUpdating(false);
    }
  };

  const handleComptaSubmit = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      await api.put(`/service-requests/${id}/comptabilite`, comptaForm);
      await loadData();
      toast.success('Comptabilité mise à jour !');
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    try {
      setSending(true);
      await api.post(`/service-requests/${id}/messages`, { message: newMessage });
      setNewMessage('');
      const messagesRes = await api.get(`/service-requests/${id}/messages`);
      setMessages(Array.isArray(messagesRes.data) ? messagesRes.data : []);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (statut) => {
    const colors = {
      'en_attente': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'en_cours': 'bg-blue-100 text-blue-700 border-blue-300',
      'paiement_attente': 'bg-orange-100 text-orange-700 border-orange-300',
      'paiement_recu': 'bg-teal-100 text-teal-700 border-teal-300',
      'fourniture': 'bg-purple-100 text-purple-700 border-purple-300',
      'termine': 'bg-green-100 text-green-700 border-green-300',
      'annule': 'bg-gray-100 text-gray-700 border-gray-300',
      'refuse': 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getPriorityBadge = (priorite) => {
    const badges = {
      'basse': 'bg-green-100 text-green-700',
      'normale': 'bg-yellow-100 text-yellow-700',
      'haute': 'bg-orange-100 text-orange-700',
      'urgente': 'bg-red-100 text-red-700'
    };
    return badges[priorite] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <DjamannLoader variant="fullscreen" />;
  }

  if (!request) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <span className="text-6xl block mb-4"><Frown className="w-16 h-16 mx-auto text-gray-400" /></span>
          <p className="text-gray-500 mb-4">Demande non trouvée</p>
          <Link to="/admin/service-requests" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            Retour aux demandes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-10 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <Link to="/admin/service-requests" className="text-indigo-600 hover:underline text-sm mb-2 block">
              ← Retour aux demandes
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">
              Demande #{request.id} - {request.titre}
            </h1>
          </div>
          <div className="flex items-center gap-4 mt-4 md:mt-0">
            <span className={`px-4 py-2 rounded-full font-medium border ${getStatusBadge(request.statut_nom)}`}>
              {request.statut_nom?.replace('_', ' ')}
            </span>
            <span className={`px-4 py-2 rounded-full font-medium ${getPriorityBadge(request.priorite)}`}>
              {request.priorite}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            {/* Détails de la demande */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 inline" /> Détails de la demande
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Service demandé</p>
                  <div className="flex items-center gap-2 mt-1">
                    {request.logo && (
                      <img 
                        src={getLogoUrl(request.logo)}
                        alt=""
                        className="w-10 h-10 object-contain bg-gray-100 rounded"
                      />
                    )}
                    <span className="font-medium text-gray-800">{request.service_titre}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date de demande</p>
                  <p className="font-medium text-gray-800">{formatDate(request.date_demande)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date souhaitée</p>
                  <p className="font-medium text-gray-800">
                    {request.date_souhaitee ? formatDate(request.date_souhaitee) : 'Non spécifiée'}
                  </p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-2">Description</p>
                <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700">
                  {request.description}
                </div>
              </div>
            </div>

            {/* Informations client */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 inline" /> Informations client
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Nom</p>
                  <p className="font-medium text-gray-800">{request.user_nom}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <a href={`mailto:${request.user_email}`} className="font-medium text-indigo-600 hover:underline">
                    {request.user_email}
                  </a>
                </div>
                <div>
                  <p className="text-sm text-gray-500">ID Utilisateur</p>
                  <p className="font-medium text-gray-800">#{request.user_id}</p>
                </div>
              </div>
            </div>

            {/* Messagerie */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="bg-indigo-600 text-white px-6 py-4">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 inline" /> Messagerie avec le client
                </h2>
              </div>
              
              <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-50">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <span className="text-4xl block mb-2"><MessageCircle className="w-10 h-10 mx-auto text-gray-400" /></span>
                    <p>Aucun message. Démarrez la conversation !</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${String(msg.sender_id) === String(user?.id) ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                          String(msg.sender_id) === String(user?.id)
                            ? 'bg-indigo-600 text-white rounded-br-sm'
                            : 'bg-white text-gray-800 shadow rounded-bl-sm'
                        }`}
                      >
                        <p className="whitespace-pre-wrap text-sm">{msg.message}</p>
                        <p className={`text-xs mt-1 ${String(msg.sender_id) === String(user?.id) ? 'text-indigo-200' : 'text-gray-400'}`}>
                          {new Date(msg.created_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
              
              <form onSubmit={handleSendMessage} className="p-4 border-t bg-white">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Votre réponse au client..."
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition"
                  >
                    {sending ? '...' : 'Envoyer'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Changer le statut */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 inline" /> Changer le statut
              </h3>
              <div className="space-y-2">
                {statuses.map(status => (
                  <button
                    key={status.id}
                    onClick={() => handleStatusChange(status.id)}
                    disabled={updating || request.statut_id === status.id}
                    className={`w-full px-4 py-3 rounded-lg text-sm font-medium transition border ${
                      request.statut_id === status.id
                        ? `${getStatusBadge(status.nom)} border-2`
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-gray-200'
                    } ${updating ? 'opacity-50' : ''}`}
                  >
                    {status.nom.replace('_', ' ')}
                    {request.statut_id === status.id && <> <Check className="w-3 h-3 inline" /></>}
                  </button>
                ))}
              </div>
            </div>

            {/* Comptabilité */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 inline" /> Comptabilité
              </h3>
              <form onSubmit={handleComptaSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Montant estimé (XOF)</label>
                  <input
                    type="number"
                    value={comptaForm.montant_estime}
                    onChange={(e) => setComptaForm({ ...comptaForm, montant_estime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    placeholder="Ex: 50000"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Montant final (XOF)</label>
                  <input
                    type="number"
                    value={comptaForm.montant_final}
                    onChange={(e) => setComptaForm({ ...comptaForm, montant_final: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    placeholder="Ex: 45000"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Montant payé (XOF)</label>
                  <input
                    type="number"
                    value={comptaForm.montant_paye}
                    onChange={(e) => setComptaForm({ ...comptaForm, montant_paye: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    placeholder="Ex: 45000"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Mode de paiement</label>
                  <select
                    value={comptaForm.mode_paiement}
                    onChange={(e) => setComptaForm({ ...comptaForm, mode_paiement: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                  >
                    <option value="">Sélectionner</option>
                    <option value="especes">Espèces</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="virement">Virement bancaire</option>
                    <option value="carte">Carte bancaire</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Référence paiement</label>
                  <input
                    type="text"
                    value={comptaForm.reference_paiement}
                    onChange={(e) => setComptaForm({ ...comptaForm, reference_paiement: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    placeholder="Ex: TRX-123456"
                  />
                </div>
                
                {/* Récapitulatif */}
                {comptaForm.montant_final && (
                  <div className="bg-gray-50 rounded-lg p-3 mt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Montant final:</span>
                      <span className="font-bold">{Number(comptaForm.montant_final).toLocaleString()} XOF</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Payé:</span>
                      <span className="font-bold text-green-600">{Number(comptaForm.montant_paye || 0).toLocaleString()} XOF</span>
                    </div>
                    <div className="flex justify-between text-sm border-t mt-2 pt-2">
                      <span className="text-gray-600">Reste à payer:</span>
                      <span className={`font-bold ${Number(comptaForm.montant_final) - Number(comptaForm.montant_paye || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {Math.max(0, Number(comptaForm.montant_final) - Number(comptaForm.montant_paye || 0)).toLocaleString()} XOF
                      </span>
                    </div>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={updating}
                  className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition font-medium"
                >
                  {updating ? 'Mise à jour...' : <><Save className="w-4 h-4 inline" /> Enregistrer</>}
                </button>
              </form>
            </div>

            {/* Historique */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <ScrollText className="w-5 h-5 inline" /> Historique
              </h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {request.historique && request.historique.length > 0 ? (
                  request.historique.map((h, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-sm">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-gray-800">
                          <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(h.ancien_statut_nom)}`}>{h.ancien_statut_nom || 'Création'}</span>
                          {' → '}
                          <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(h.nouveau_statut_nom)}`}>{h.nouveau_statut_nom}</span>
                        </p>
                        {h.commentaire && <p className="text-gray-600 mt-1">{h.commentaire}</p>}
                        <p className="text-gray-400 text-xs">{formatDate(h.created_at)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm text-center">Aucun historique</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminServiceRequestDetail;
